import React, { FunctionComponent } from 'react';

import {
    Alert,
    AlertIcon,
    AspectRatio,
    Box,
    Center,
    Heading,
    HStack,
    Icon,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Tooltip,
    VStack
} from '@chakra-ui/react';
import { FaRegPlayCircle } from 'react-icons/fa';
import { ImEyeBlocked } from 'react-icons/im';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactPlayer from 'react-player';

import { EventScheduleType, EventType } from '~/components/Event';
import EventHeader from '~/components/Event/EventHeader';
import { useLivestreamData } from '~/components/Event/provider/LivestreamDataProvider';
import SpeakerAvatar from '~/components/Speaker/SpeakerAvatar';
import { SponsorScheduleTitle } from '~/components/Sponsor/SponsorScheduleTitle';
import { Event_Schedule_Types_Enum } from '~/graphql/hasura';
import { EventScheduleNode } from '~/lib/data-transform/event-schedules';
import { commaSeperatedSpeakerNames } from '~/lib/data-transform/speakers';
import { IEventAccessController } from '~/lib/firebase';

export const getChildrenWithAccess = (
    eventAccess: IEventAccessController,
    es: EventScheduleNode
): EventScheduleNode[] => {
    return es.children.filter((child) => eventAccess.canViewSchedule(child, 'ondemand'));
};

export const hasOndemandData = (
    es: EventScheduleNode | EventScheduleType,
    search_descendants = false
): boolean => {
    if (es.ondemand_seconds || es.ondemand_source?.src) {
        return true;
    }

    if (search_descendants && 'children' in es) {
        return es.children.filter((child) => hasOndemandData(child, search_descendants)).length > 0;
    }

    return false;
};

export const isPlayableSchedule = (schedule: EventScheduleType) => {
    return [Event_Schedule_Types_Enum.Lecture, Event_Schedule_Types_Enum.Symposium].includes(
        schedule.type_id as Event_Schedule_Types_Enum
    );
};

interface IPlaylistItem {
    schedule: EventScheduleNode;
    activeSchedule: EventScheduleType | undefined;
    onPlaylistItemClick?: (es: EventScheduleType) => void;
}

const PlaylistItem: FunctionComponent<IPlaylistItem> = ({
    schedule,
    activeSchedule,
    onPlaylistItemClick
}) => {
    const { eventAccess, event } = useLivestreamData();
    const intl = useIntl();

    if (!isPlayableSchedule(schedule)) {
        if (schedule.type_id === Event_Schedule_Types_Enum.TopicBlock) {
            if (!hasOndemandData(schedule, true)) {
                return null;
            }

            return (
                <Box py="4">
                    <Box
                        pb="2"
                        mb="2"
                        borderBottom="1px solid"
                        borderBottomColor="gray.400"
                        color="gray.600"
                    >
                        <Text
                            color="highlight"
                            fontWeight="bold"
                            fontSize="md"
                            textTransform="uppercase"
                        >
                            {schedule.title}
                        </Text>
                        <Text fontSize="sm">{commaSeperatedSpeakerNames(schedule.speakers)}</Text>
                    </Box>
                    {schedule.children.map((es) => {
                        return (
                            <PlaylistItem
                                key={es.id}
                                schedule={es}
                                activeSchedule={activeSchedule}
                                onPlaylistItemClick={onPlaylistItemClick}
                            />
                        );
                    })}
                </Box>
            );
        } else if (schedule.children.length) {
            return (
                <>
                    {schedule.children.map((es) => {
                        return (
                            <PlaylistItem
                                key={es.id}
                                schedule={es}
                                activeSchedule={activeSchedule}
                                onPlaylistItemClick={onPlaylistItemClick}
                            />
                        );
                    })}
                </>
            );
        } else {
            return null;
        }
    } else {
        if (!hasOndemandData(schedule)) {
            return null;
        }

        const isActive = activeSchedule?.id && activeSchedule.id === schedule.id;
        const hasAccess = eventAccess.canViewSchedule(schedule, 'ondemand');

        let hoverBg = isActive ? 'gray.500' : 'gray.200';

        if (!hasAccess) {
            hoverBg = 'transparent';
        }

        const noAccessText = intl.formatMessage({
            id: 'playlist.schedule.tooltip.no_access',
            defaultMessage: 'Nicht gebucht'
        });

        if (schedule.type_id === Event_Schedule_Types_Enum.Symposium && !hasAccess) {
            return null;
        }

        return (
            <Tooltip
                hasArrow
                label={noAccessText}
                aria-label={noAccessText}
                isDisabled={hasAccess}
                placement="top"
                offset={[0, -30]}
                p="5px"
            >
                <HStack
                    mb="4"
                    px="2"
                    bg={isActive && hasAccess ? 'highlight' : 'transparent'}
                    color={isActive && hasAccess ? 'highlightText' : 'inherit'}
                    _hover={{ bg: hoverBg, cursor: hasAccess ? 'pointer' : 'not-allowed' }}
                    data-id={schedule.id}
                    opacity={eventAccess.canViewSchedule(schedule, 'ondemand') ? 1 : 0.5}
                    onClick={() => {
                        if (onPlaylistItemClick && hasAccess) {
                            onPlaylistItemClick(schedule);
                        }
                    }}
                >
                    <AspectRatio
                        ratio={4 / 3}
                        minW="100px"
                        bg="black"
                        borderRadius="md"
                        mt="2"
                        alignSelf="flex-start"
                    >
                        <Box>
                            {hasAccess ? (
                                <Box height="100%" position="relative">
                                    <ReactPlayer
                                        url={schedule.ondemand_source?.src}
                                        controls={false}
                                        playing={false}
                                        width="100%"
                                        height="100%"
                                    />

                                    <Center
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        height="100%"
                                        width="100%"
                                    >
                                        {!isActive ? (
                                            <Icon
                                                color="white"
                                                fontSize="32px"
                                                as={FaRegPlayCircle}
                                            />
                                        ) : null}
                                    </Center>
                                </Box>
                            ) : (
                                <Icon color="white" fontSize="24px" as={ImEyeBlocked} />
                            )}
                        </Box>
                    </AspectRatio>
                    <Box py="2">
                        <SponsorScheduleTitle schedule={schedule} event={event} />
                        <Heading
                            size="xs"
                            dangerouslySetInnerHTML={{ __html: schedule.title }}
                        ></Heading>
                        {schedule.speakers.length ? (
                            <VStack alignItems="flex-start" mt="2">
                                {schedule.speakers.map(({ speaker }) => (
                                    <SpeakerAvatar key={speaker.id} speaker={speaker} />
                                ))}
                            </VStack>
                        ) : null}
                    </Box>
                </HStack>
            </Tooltip>
        );
    }
};

interface IPlaylistDay {
    schedules: EventScheduleNode[];
    activeSchedule: EventScheduleType | undefined;
    onPlaylistItemClick?: (es: EventScheduleType) => void;
}

const PlaylistDay: FunctionComponent<IPlaylistDay> = ({
    schedules,
    activeSchedule,
    onPlaylistItemClick
}) => {
    return (
        <Box>
            {schedules.map((schedule) => {
                return (
                    <PlaylistItem
                        key={schedule.id}
                        activeSchedule={activeSchedule}
                        schedule={schedule}
                        onPlaylistItemClick={onPlaylistItemClick}
                    />
                );
            })}
        </Box>
    );
};

interface IPlaylist {
    event: EventType;
    schedules: EventScheduleNode[];
    activeSchedule: EventScheduleType | undefined;
    onPlaylistItemClick?: (es: EventScheduleType) => void;
}

const Playlist: FunctionComponent<IPlaylist> = ({
    event,
    schedules,
    activeSchedule,
    onPlaylistItemClick
}) => {
    const { eventAccess } = useLivestreamData();

    let tabInitialIndex = 0;
    schedules.map((es, index) => {
        if (
            typeof tabInitialIndex === 'undefined' &&
            getChildrenWithAccess(eventAccess, es).length > 0
        ) {
            tabInitialIndex = index;
        }
    });

    return (
        <Box bg="gray.100" mx="4" borderRadius="lg" h="100%">
            <EventHeader event={event} layout="compact" mb="4" p="4" bg="gray.200" />
            <Tabs size="sm" colorScheme="brand" isFitted defaultIndex={tabInitialIndex}>
                <TabList mb="1em">
                    {schedules.map((es, index) => {
                        return (
                            <Tab key={es.id}>
                                <FormattedMessage
                                    id="playlist.tabs.day"
                                    defaultMessage="Tag {day_index}"
                                    values={{
                                        day_index: index + 1
                                    }}
                                />
                            </Tab>
                        );
                    })}
                </TabList>
                <TabPanels height="65vh" overflow="scroll">
                    {schedules.map((es) => {
                        const hasChildrenWithAccess =
                            getChildrenWithAccess(eventAccess, es).length > 0;

                        return (
                            <TabPanel px="0" key={es.id}>
                                {!hasChildrenWithAccess ? (
                                    <Alert status="warning">
                                        <AlertIcon />
                                        <FormattedMessage
                                            id="playlist.day.no_access"
                                            defaultMessage="Tag nicht gebucht."
                                        />
                                    </Alert>
                                ) : null}
                                <PlaylistDay
                                    schedules={es.children}
                                    activeSchedule={activeSchedule}
                                    onPlaylistItemClick={onPlaylistItemClick}
                                />
                            </TabPanel>
                        );
                    })}
                </TabPanels>
            </Tabs>
        </Box>
    );
};

export default Playlist;
